import React, { useState, useEffect } from 'react'
import { Button, Dialog, Toast, Form, Input } from 'react-vant'
import {
  Arrow,
  Lock,
  ShieldO,
  ServiceO,
  ChartTrendingO,
  MedalO,
  Records,
  VolumeO,
  InfoO,
} from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { logout } from '../../store/userSlice'
import request from '../../api/request'
import { displayRole as getDisplayRole, isAdmin as checkAdmin, isPersonal } from '../../utils/permissions'

export default function Profile() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { userInfo } = useSelector(state => state.user)

  const [passwordShow, setPasswordShow] = useState(false)
  const [passwordForm] = Form.useForm()
  const [studentInfo, setStudentInfo] = useState(null)

  // 网络与节点状态测试
  const [serverModalVisible, setServerModalVisible] = useState(false)
  const [testingPing, setTestingPing] = useState(false)
  const [pingResult, setPingResult] = useState(null)

  const isAdmin = checkAdmin(userInfo)
  const isStudent = isPersonal(userInfo)
  const isTeacher = !isStudent

  // 若为学生/家长，异步获取绑定的子女或学生学籍数据
  useEffect(() => {
    if (isStudent) {
      const fetchStudentData = async () => {
        try {
          const res = await request.get('/academic/students/m/children/')
          if (res?.data && res.data.length > 0) {
            setStudentInfo(res.data[0])
          }
        } catch {
          // ignore
        }
      }
      fetchStudentData()
    }
  }, [isStudent])

  // 退出登录二次确认
  const handleLogout = () => {
    Dialog.confirm({
      title: '确认退出登录',
      message: '退出当前账号后需重新验证身份登录，确定退出吗？',
      confirmButtonText: '退出登录',
      cancelButtonText: '取消',
    }).then(() => {
      dispatch(logout())
      Toast.success('已安全退出登录')
      navigate('/login', { replace: true })
    }).catch(() => { })
  }

  // 提交修改密码
  const onChangePassword = async (values) => {
    try {
      if (values.new_password !== values.confirm_password) {
        Toast.fail('两次输入的新密码不一致')
        return
      }
      await request.post('/auth/change-password/', {
        current_password: values.current_password,
        new_password: values.new_password
      })
      Toast.success('密码修改成功，请重新登录')
      setPasswordShow(false)
      setTimeout(() => {
        dispatch(logout())
        navigate('/login', { replace: true })
      }, 1500)
    } catch (e) {
      console.error(e)
      Toast.fail(e.response?.data?.detail || '修改失败，请核对当前原密码')
    }
  }

  // 测试与服务端的连通性
  const handleTestConnection = async () => {
    setTestingPing(true)
    setPingResult(null)
    const t0 = Date.now()
    try {
      await request.get('/health', { timeout: 3500 })
      const latency = Date.now() - t0
      setPingResult({ success: true, latency })
    } catch {
      setPingResult({ success: false })
    } finally {
      setTestingPing(false)
    }
  }

  // 用户与角色信息
  const displayName = studentInfo?.real_name || userInfo?.real_name || userInfo?.username || '用户'
  const displayRoleText = getDisplayRole(userInfo)
  const roleColor = isAdmin ? '#7c3aed' : userInfo?.role === 'teacher' ? '#056b4f' : '#059669'
  const roleBg = isAdmin ? '#f5f3ff' : userInfo?.role === 'teacher' ? '#f0fdf4' : '#ecfdf5'

  const gradeName = studentInfo?.grade_name || studentInfo?.grade || userInfo?.grade || '二年级'
  const className = studentInfo?.class_name || userInfo?.class_name || '主校区1班'
  const educationId = studentInfo?.education_id || userInfo?.education_id || userInfo?.username || '—'

  return (
    <div className="min-h-screen bg-[#f8fafc] text-[#0f172a] px-4 pt-5 pb-28 font-sans select-none max-w-lg mx-auto">
      {/* ========== 1. 顶部个人凭据卡 (Hero Profile Card) ========== */}
      <div className="bg-white rounded-2xl p-5 border border-[#e2e8f0] shadow-sm flex items-center gap-4 mb-4">
        {/* 头像 + 在线状态 */}
        <div className="relative shrink-0">
          <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-[#056b4f] to-[#34d399] text-white flex items-center justify-center text-[22px] font-extrabold shadow-md">
            {displayName[0] || '用'}
          </div>
          <span className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-[#00b578] border-2 border-white" />
        </div>

        {/* 名字 + 角色徽章 + 学校 + 账号 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[18px] font-extrabold text-[#0f172a] truncate">
              {displayName}
            </span>
            <span
              style={{ background: roleBg, color: roleColor }}
              className="text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0"
            >
              {displayRoleText}
            </span>
          </div>

          <div className="text-[12.5px] text-[#475569] flex items-center gap-1 mb-1 truncate">
            <span className="text-[14px]">🏢</span>
            <span className="truncate">{userInfo?.school_name || '北京市丰台区东高地第二小学'}</span>
          </div>

          <div className="text-[11.5px] text-[#94a3b8] font-mono">
            账号: {userInfo?.username || '—'}
          </div>
        </div>
      </div>

      {/* ========== 2. 基本信息 (Basic Profile Info) ========== */}
      <div className="mb-4">
        <div className="text-[13px] font-bold text-[#64748b] mb-2 px-1">
          基本信息
        </div>

        <div className="bg-white rounded-2xl border border-[#e2e8f0] overflow-hidden divide-y divide-[#f1f5f9]">
          {isStudent ? (
            <>
              {/* 年级 */}
              <div className="flex justify-between items-center px-4 py-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#f5f3ff] text-[#7c3aed] flex items-center justify-center text-[15px]">
                    🎖️
                  </div>
                  <span className="text-[14px] font-medium text-[#0f172a]">就读年级</span>
                </div>
                <span className="text-[14px] text-[#64748b] font-medium">{gradeName}</span>
              </div>

              {/* 班级 */}
              <div className="flex justify-between items-center px-4 py-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#f0fdf4] text-[#056b4f] flex items-center justify-center text-[15px]">
                    🎓
                  </div>
                  <span className="text-[14px] font-medium text-[#0f172a]">所在班级</span>
                </div>
                <span className="text-[14px] text-[#64748b] font-medium">{className}</span>
              </div>

              {/* 教育 ID */}
              <div className="flex justify-between items-center px-4 py-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#ecfdf5] text-[#059669] flex items-center justify-center text-[15px]">
                    📱
                  </div>
                  <span className="text-[14px] font-medium text-[#0f172a]">教育 ID / 学籍</span>
                </div>
                <span className="text-[14px] text-[#64748b] font-mono">{educationId}</span>
              </div>
            </>
          ) : (
            <>
              {/* 教师任教学科 */}
              <div className="flex justify-between items-center px-4 py-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#f0fdf4] text-[#056b4f] flex items-center justify-center text-[15px]">
                    🏃
                  </div>
                  <span className="text-[14px] font-medium text-[#0f172a]">任教学科</span>
                </div>
                <span className="text-[14px] text-[#64748b] font-medium">
                  {userInfo?.teaching_subject || '体育与健康'}
                </span>
              </div>

              {/* 教研组部门 */}
              <div className="flex justify-between items-center px-4 py-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#f5f3ff] text-[#7c3aed] flex items-center justify-center text-[15px]">
                    📋
                  </div>
                  <span className="text-[14px] font-medium text-[#0f172a]">教研组 / 部门</span>
                </div>
                <span className="text-[14px] text-[#64748b] font-medium">
                  {userInfo?.teaching_group || '体育教研组'}
                </span>
              </div>

              {/* 职务职责 */}
              <div className="flex justify-between items-center px-4 py-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#ecfdf5] text-[#059669] flex items-center justify-center text-[15px]">
                    💼
                  </div>
                  <span className="text-[14px] font-medium text-[#0f172a]">岗位职责</span>
                </div>
                <span className="text-[14px] text-[#64748b] font-medium">
                  {userInfo?.duty_role || (isAdmin ? '系统全面管理与维护' : '体育教学与健康体测')}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ========== 3. 角色专属应用区 ========== */}
      {isAdmin && (
        <div className="mb-4">
          <div className="text-[13px] font-bold text-[#64748b] mb-2 px-1">
            系统管理中心
          </div>

          <div className="bg-white rounded-2xl border border-[#e2e8f0] overflow-hidden">
            {/* 通知中心 */}
            <div
              onClick={() => navigate('/admin/notification-release')}
              className="flex justify-between items-center px-4 py-3.5 cursor-pointer active:bg-[#f8fafc] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#f0fdf4] text-[#056b4f] flex items-center justify-center text-[16px]">
                  📢
                </div>
                <div>
                  <div className="text-[14px] font-bold text-[#0f172a]">通知中心</div>
                  <div className="text-[11.5px] text-[#94a3b8]">下发体测、运动会及教务考勤通告</div>
                </div>
              </div>
              <Arrow style={{ fontSize: 14, color: '#94a3b8' }} />
            </div>
          </div>
        </div>
      )}

      {isStudent && (
        <div className="mb-4">
          <div className="text-[13px] font-bold text-[#64748b] mb-2 px-1">
            体测与个人成长档案
          </div>

          <div className="bg-white rounded-2xl border border-[#e2e8f0] overflow-hidden divide-y divide-[#f1f5f9]">
            {/* 体测成绩查询 */}
            <div
              onClick={() => navigate('/student/physical-test/query')}
              className="flex justify-between items-center px-4 py-3.5 cursor-pointer active:bg-[#f8fafc] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#f0fdf4] text-[#056b4f] flex items-center justify-center">
                  <MedalO style={{ fontSize: 18 }} />
                </div>
                <span className="text-[14px] font-medium text-[#0f172a]">体测成绩明细查询</span>
              </div>
              <Arrow style={{ fontSize: 14, color: '#94a3b8' }} />
            </div>

            {/* 健康成长档案 */}
            <div
              onClick={() => navigate('/student/physical-test/portfolio')}
              className="flex justify-between items-center px-4 py-3.5 cursor-pointer active:bg-[#f8fafc] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#ecfdf5] text-[#059669] flex items-center justify-center">
                  <ChartTrendingO style={{ fontSize: 18 }} />
                </div>
                <span className="text-[14px] font-medium text-[#0f172a]">健康成长五维档案</span>
              </div>
              <Arrow style={{ fontSize: 14, color: '#94a3b8' }} />
            </div>

            {/* 体测免测申请 */}
            <div
              onClick={() => navigate('/student/physical-test/exemption')}
              className="flex justify-between items-center px-4 py-3.5 cursor-pointer active:bg-[#f8fafc] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#fff7ed] text-[#ea580c] flex items-center justify-center">
                  <ShieldO style={{ fontSize: 18 }} />
                </div>
                <span className="text-[14px] font-medium text-[#0f172a]">体测免测申请办理</span>
              </div>
              <Arrow style={{ fontSize: 14, color: '#94a3b8' }} />
            </div>
          </div>
        </div>
      )}

      {/* ========== 4. 系统与安全 (System & Security - 全角色通用) ========== */}
      <div className="mb-6">
        <div className="text-[13px] font-bold text-[#64748b] mb-2 px-1">
          系统与安全
        </div>

        <div className="bg-white rounded-2xl border border-[#e2e8f0] overflow-hidden divide-y divide-[#f1f5f9]">
          {/* 账号安全 */}
          <div
            onClick={() => setPasswordShow(true)}
            className="flex justify-between items-center px-4 py-3.5 cursor-pointer active:bg-[#f8fafc] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-[#f1f5f9] text-[#475569] flex items-center justify-center">
                <Lock style={{ fontSize: 17 }} />
              </div>
              <span className="text-[14px] font-medium text-[#0f172a]">账号安全（修改密码）</span>
            </div>
            <Arrow style={{ fontSize: 14, color: '#94a3b8' }} />
          </div>

          {/* 网络与连接配置 */}
          <div
            onClick={() => setServerModalVisible(true)}
            className="flex justify-between items-center px-4 py-3.5 cursor-pointer active:bg-[#f8fafc] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-[#ecfdf5] text-[#059669] flex items-center justify-center text-[15px]">
                🌐
              </div>
              <span className="text-[14px] font-medium text-[#0f172a]">网络连接与同步状态</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[12.5px] text-[#059669] font-medium flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-[#00b578] inline-block animate-pulse"></span>
                专网集群
              </span>
              <Arrow style={{ fontSize: 14, color: '#94a3b8' }} />
            </div>
          </div>

          {/* 站内消息通知 */}
          <div
            onClick={() => navigate('/notifications')}
            className="flex justify-between items-center px-4 py-3.5 cursor-pointer active:bg-[#f8fafc] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-[#f0fdf4] text-[#056b4f] flex items-center justify-center">
                <VolumeO style={{ fontSize: 17 }} />
              </div>
              <span className="text-[14px] font-medium text-[#0f172a]">站内消息通知</span>
            </div>
            <Arrow style={{ fontSize: 14, color: '#94a3b8' }} />
          </div>

          {/* 帮助与用户反馈 */}
          <div
            onClick={() => navigate('/feedback')}
            className="flex justify-between items-center px-4 py-3.5 cursor-pointer active:bg-[#f8fafc] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-[#fff7ed] text-[#ea580c] flex items-center justify-center">
                <ServiceO style={{ fontSize: 17 }} />
              </div>
              <span className="text-[14px] font-medium text-[#0f172a]">帮助与问题反馈</span>
            </div>
            <Arrow style={{ fontSize: 14, color: '#94a3b8' }} />
          </div>

          {/* 关于系统 */}
          <div
            onClick={() => Toast.info('当前版本: v1.10.0 (2026 稳定版) · 已是最新版本')}
            className="flex justify-between items-center px-4 py-3.5 cursor-pointer active:bg-[#f8fafc] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-[#f8fafc] text-[#64748b] flex items-center justify-center">
                <InfoO style={{ fontSize: 17 }} />
              </div>
              <span className="text-[14px] font-medium text-[#0f172a]">关于系统</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[12.5px] text-[#94a3b8]">v1.10.0 稳定版</span>
              <Arrow style={{ fontSize: 14, color: '#94a3b8' }} />
            </div>
          </div>
        </div>
      </div>

      {/* ========== 5. 退出登录按钮 ========== */}
      <button
        type="button"
        onClick={handleLogout}
        className="w-full h-12 rounded-2xl bg-[#fee2e2] text-[#e11d48] font-bold text-[15px] border border-[#fecaca] active:bg-[#fecaca] transition-all flex items-center justify-center shadow-sm"
      >
        退出当前登录账号
      </button>

      {/* ========== 6. 修改密码对话框 ========== */}
      <Dialog
        visible={passwordShow}
        title="修改登录密码"
        showCancelButton
        confirmButtonText="确认修改"
        cancelButtonText="取消"
        onCancel={() => setPasswordShow(false)}
        onConfirm={() => passwordForm.submit()}
      >
        <div className="p-4">
          <Form
            form={passwordForm}
            onFinish={onChangePassword}
            footer={null}
          >
            <Form.Item
              name="current_password"
              label="当前密码"
              rules={[{ required: true, message: '请输入当前密码' }]}
            >
              <Input type="password" placeholder="请输入当前旧密码" />
            </Form.Item>
            <Form.Item
              name="new_password"
              label="新密码"
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 6, message: '密码不能少于6位' }
              ]}
            >
              <Input type="password" placeholder="请输入至少6位新密码" />
            </Form.Item>
            <Form.Item
              name="confirm_password"
              label="确认密码"
              rules={[{ required: true, message: '请再次输入新密码' }]}
            >
              <Input type="password" placeholder="请再次输入新密码" />
            </Form.Item>
          </Form>
        </div>
      </Dialog>

      {/* ========== 7. 网络连接与节点配置对话框 ========== */}
      <Dialog
        visible={serverModalVisible}
        title="网络连接与节点配置"
        showConfirmButton
        confirmButtonText="完成"
        onConfirm={() => setServerModalVisible(false)}
      >
        <div className="p-4 space-y-3 text-[13px]">
          <div className="p-3.5 bg-[#f8fafc] rounded-xl border border-[#e2e8f0] space-y-2">
            <div className="flex justify-between items-center text-[#64748b]">
              <span>当前接入节点</span>
              <span className="font-bold text-[#0f172a]">校园高速专网 / 云端集群</span>
            </div>
            <div className="flex justify-between items-center text-[#64748b]">
              <span>服务主机</span>
              <span className="font-mono text-[#056b4f] font-bold">m.d2ty.cn</span>
            </div>
            <div className="flex justify-between items-center text-[#64748b]">
              <span>数据同步通道</span>
              <span className="text-[#059669] font-bold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#00b578] inline-block animate-pulse"></span>
                实时双向同步就绪
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              disabled={testingPing}
              onClick={handleTestConnection}
              className="px-3.5 py-2 rounded-xl bg-[#056b4f] text-white text-[12px] font-bold active:bg-[#04523c] transition-all flex items-center gap-1.5 shadow-sm"
            >
              {testingPing ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>测速中...</span>
                </>
              ) : (
                <span>测试网络连通性</span>
              )}
            </button>
            {pingResult && (
              <span className={`text-[12px] font-bold ${pingResult.success ? 'text-[#059669]' : 'text-[#e11d48]'}`}>
                {pingResult.success ? `网络畅通 (${pingResult.latency}ms)` : '连接超时或异常'}
              </span>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  )
}
